import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Button,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    DocumentIcon,
    DocumentTextIcon,
    PhotoIcon,
    TableCellsIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import dayjs from 'dayjs';
import { useState } from 'react';
import DocumentUploadPanel from './components/DocumentUploadPanel';
import EmployeeShowLayout from './layouts/EmployeeShowLayout';

const FILE_ICONS = {
    pdf: { Icon: DocumentTextIcon, className: 'text-red-600' },
    doc: { Icon: DocumentTextIcon, className: 'text-blue-600' },
    docx: { Icon: DocumentTextIcon, className: 'text-blue-600' },
    xls: { Icon: TableCellsIcon, className: 'text-green-600' },
    xlsx: { Icon: TableCellsIcon, className: 'text-green-600' },
    csv: { Icon: TableCellsIcon, className: 'text-green-600' },
    jpg: { Icon: PhotoIcon, className: 'text-teal-600' },
    jpeg: { Icon: PhotoIcon, className: 'text-teal-600' },
    png: { Icon: PhotoIcon, className: 'text-teal-600' },
};

const FileIcon = ({ extension }) => {
    const { Icon, className } = FILE_ICONS[extension?.toLowerCase()] ?? {
        Icon: DocumentIcon,
        className: 'text-gray-400',
    };

    return <Icon className={`size-5 shrink-0 ${className}`} aria-hidden />;
};

const DocumentView = () => {
    const { auth, employee } = usePage().props;

    const documents = [...(employee?.documents ?? [])].sort((a, b) =>
        dayjs(b.created_at).diff(dayjs(a.created_at))
    );
    const canDownload = auth?.permissions?.includes('download documents');
    const canUpload = auth?.permissions?.includes('create documents');
    const canDelete = auth?.permissions?.includes('delete documents');
    const { confirm, setLoading } = useConfirmationStore();

    const [uploading, setUploading] = useState(false);

    const handleDelete = document =>
        confirm({
            title: 'Document verwijderen?',
            description: `"${document.name}" wordt verwijderd. Het bestand is daarna niet meer te downloaden. Dit kun je niet ongedaan maken.`,
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                setLoading(true);

                router.delete(
                    `/customers/${employee.customer_id}/employees/${employee.id}/documents/${document.id}`,
                    { preserveScroll: true, onFinish: close }
                );
            },
        });

    return (
        <>
            <Segment>
                <SegmentHeader header={`Documenten (${documents.length})`}>
                    {canUpload && (
                        <Button
                            type='button'
                            variant='primary'
                            onClick={() => setUploading(true)}
                        >
                            <ArrowUpTrayIcon className='size-4' />
                            Uploaden
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    {documents.length === 0 ? (
                        <p className='text-sm text-gray-500'>
                            Deze werknemer heeft nog geen documenten.
                        </p>
                    ) : (
                        <Table striped>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>Document</TableHeaderCell>
                                    <TableHeaderCell>Soort</TableHeaderCell>
                                    <TableHeaderCell>Datum</TableHeaderCell>
                                    <TableHeaderCell>
                                        Geüpload door
                                    </TableHeaderCell>
                                    {(canDownload || canDelete) && (
                                        <TableHeaderCell>
                                            <span className='sr-only'>
                                                Acties
                                            </span>
                                        </TableHeaderCell>
                                    )}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {documents.map(document => (
                                    <TableRow key={document.id}>
                                        <TableCell>
                                            <div className='flex items-center gap-3'>
                                                <FileIcon
                                                    extension={
                                                        document.extension
                                                    }
                                                />
                                                <p className='min-w-0 truncate font-medium text-gray-900'>
                                                    {document.name}
                                                </p>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {document.label ? (
                                                <Badge color='gray'>
                                                    {document.label}
                                                </Badge>
                                            ) : (
                                                <span className='text-gray-400'>
                                                    Ongesorteerd
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className='whitespace-nowrap'>
                                            {localDate(document.created_at)}
                                        </TableCell>
                                        <TableCell>
                                            {document.user?.name ?? (
                                                <span className='text-gray-400'>
                                                    Systeem
                                                </span>
                                            )}
                                        </TableCell>
                                        {(canDownload || canDelete) && (
                                            <TableCell textAlign='right'>
                                                <div className='flex justify-end gap-2'>
                                                    {/* The legacy download refuses XML files. */}
                                                    {canDownload &&
                                                        document.extension !==
                                                            'xml' && (
                                                            <Button
                                                                as='a'
                                                                href={`/documents/${document.id}`}
                                                                aria-label={`${document.name} downloaden`}
                                                            >
                                                                <ArrowDownTrayIcon className='size-4' />
                                                            </Button>
                                                        )}
                                                    {canDelete && (
                                                        <Button
                                                            type='button'
                                                            variant='danger'
                                                            aria-label={`${document.name} verwijderen`}
                                                            onClick={() =>
                                                                handleDelete(
                                                                    document
                                                                )
                                                            }
                                                        >
                                                            <TrashIcon className='size-4' />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </SegmentContent>
            </Segment>

            {uploading && (
                <DocumentUploadPanel onClose={() => setUploading(false)} />
            )}
        </>
    );
};

DocumentView.layout = page => <EmployeeShowLayout>{page}</EmployeeShowLayout>;

export default DocumentView;
